/*
  ADA #192 - The announcement bar is a carousel once it holds two or more announcements.
  Swiper only moves the slides; everything a keyboard or screen reader user needs is
  handled here: previous/next buttons, aria-hidden and tabindex="-1" on the slides that
  are out of view, a live region that only speaks when the bar is not rotating on its
  own, and auto-rotation that respects the section setting and prefers-reduced-motion,
  pauses on hover and focus, and stops for good when the visitor uses a button.
*/
(function () {
  function initAnnouncementBar(section) {
    const container = section.querySelector('.custom-announcement-bar-swiper-container');
    const slides = Array.from(section.querySelectorAll('.swiper-slide'));
    if (!container || slides.length < 2 || typeof Swiper === 'undefined') return;
    if (section.dataset.carouselReady) return;
    section.dataset.carouselReady = 'true';

    const prevButton = section.querySelector('.custom-announcement-bar__button--prev');
    const nextButton = section.querySelector('.custom-announcement-bar__button--next');
    const pauseButton = section.querySelector('.custom-announcement-bar__button--pause');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = section.dataset.autoplay === 'true' && !reducedMotion;
    const autoplayDelay = parseInt(section.dataset.autoplaySpeed, 10) || 5000;
    // Three independent reasons hold the rotation (WAI-ARIA carousel pattern): the visitor pressed
    // pause, previous or next; the pointer is over the bar; focus is inside it. The bar rotates only
    // while none of them applies. The pause button reflects the first one alone.
    let stoppedByVisitor = false;
    let hovered = false;
    let focused = false;

    const swiper = new Swiper(container, {
      rewind: true,
      speed: reducedMotion ? 0 : 500,
      a11y: false,
      autoplay: autoplayEnabled ? { delay: autoplayDelay, disableOnInteraction: false } : false,
      navigation: { prevEl: prevButton, nextEl: nextButton },
    });

    const isRotating = () => Boolean(autoplayEnabled && swiper.autoplay && swiper.autoplay.running);

    function syncSlides() {
      slides.forEach((slide, index) => {
        const visible = index === swiper.activeIndex;
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((element) => {
          if (visible) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    }

    function syncControls() {
      container.setAttribute('aria-live', isRotating() ? 'off' : 'polite');
      if (!pauseButton) return;
      // The button reflects the visitor's choice, not the momentary pause while hovering or focused.
      pauseButton.classList.toggle('is-paused', stoppedByVisitor);
      pauseButton.setAttribute(
        'aria-label',
        stoppedByVisitor ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause
      );
    }

    function updateRotation() {
      if (!autoplayEnabled) return;
      const shouldRotate = !stoppedByVisitor && !hovered && !focused;
      if (shouldRotate && !swiper.autoplay.running) swiper.autoplay.start();
      if (!shouldRotate && swiper.autoplay.running) swiper.autoplay.stop();
      syncControls();
    }

    swiper.on('slideChange', syncSlides);
    syncSlides();
    syncControls();

    if (!autoplayEnabled) {
      if (pauseButton) pauseButton.hidden = true;
      return;
    }

    // Using a button is a deliberate choice: the bar stays put until Play is pressed.
    [prevButton, nextButton].forEach((button) => {
      button.addEventListener('click', () => {
        stoppedByVisitor = true;
        updateRotation();
      });
    });

    if (pauseButton) {
      // Play records the choice at once (the button flips back to Pause); the rotation itself
      // resumes when the pointer and the focus have both left the bar.
      pauseButton.addEventListener('click', () => {
        stoppedByVisitor = !stoppedByVisitor;
        updateRotation();
      });
    }

    section.addEventListener('mouseenter', () => {
      hovered = true;
      updateRotation();
    });
    section.addEventListener('mouseleave', () => {
      hovered = false;
      updateRotation();
    });
    section.addEventListener('focusin', () => {
      focused = true;
      updateRotation();
    });
    section.addEventListener('focusout', (event) => {
      if (section.contains(event.relatedTarget)) return;
      focused = false;
      updateRotation();
    });
  }

  function init(root) {
    // The section wrapper carries the same class; the bar itself is the element with the data attributes.
    root.querySelectorAll('.custom-announcement-bar-section[data-autoplay]').forEach(initAnnouncementBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // The theme editor swaps the section markup after a setting or block change without running
  // this script again, so the replacement bar is initialised from the event Shopify fires.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // The editor fires this before it removes the old markup. Without it the previous Swiper would
  // keep its autoplay timer and its document listeners alive on a bar that is no longer on the page.
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.custom-announcement-bar-swiper-container').forEach((container) => {
      if (container.swiper) container.swiper.destroy(true, true);
    });
  });
})();
